/**
 * CampaignSelect — 캠페인 선택 UI 통합 모듈
 *
 * export 구조:
 *  - 공통: CampaignItem, CampaignStatusIcon, CAMPAIGN_STATUS_ICON, CAMPAIGN_STATUS_COLOR, campaignStatusSortWeight
 *  - CampaignSelectPanel (named): 커스텀 CSS 기반 단일 선택 패널
 *  - CampaignSelect (default): shadcn 기반 단일/다중 선택 드롭다운 + 트리거 버튼
 */

import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, EyeOff, ChevronDown, Check, Users, Info } from 'lucide-react';
import { GgcInput } from '@/components/ui/ggc/input';
import { GgcButton } from '@/components/ui/ggc/button';
import { GgcCheckbox } from '@/components/ui/ggc/checkbox';
import MultiSelect, { type MultiSelectOption } from '@/components/MultiSelect';
import Tooltip from '@/components/Tooltip';
import { useI18nText } from '@/lib/utils/i18nText';
import { useAppSelector } from '@/store/hooks';
import { getRecentCampaignIds, addRecentCampaignId, removeRecentCampaignId } from '@/lib/utils/recentCampaigns';
import { useCampaignStatusStyle, CAMPAIGN_STATUS_DOT_CLASS } from '@/lib/utils/campaignStatusStyle';

/* ══════════════════════════════════════════════════════════
   공통 타입
   ══════════════════════════════════════════════════════════ */

export interface CampaignItem {
  _id: string;
  id?: string;
  name: string;
  status?: string;
  hidden?: boolean;
  campaignGenre?: string;
  userId?: string;
  updatedAt?: string;
}

export interface SelectableCampaign {
  id: string;
  name: string;
  status?: string;
  hidden?: boolean;
  userId?: string;
  campaignGenre?: string;
}

export interface CampaignGenreOption {
  _id: string;
  title: string;
}

interface CampaignGenre {
  _id: string;
  title?: string;
}

/* ══════════════════════════════════════════════════════════
   공통 상수 / 유틸
   ══════════════════════════════════════════════════════════ */

export const CAMPAIGN_STATUS_ICON: Record<string, string> = {
  roster: 'fa-th-list',
  template: 'fa-file-text',
  private_draft: 'fa-eye-slash',
  draft: 'fa-pencil-square',
  in_progress: 'fa-running',
  completed: 'fa-check',
  trashed: 'fa-trash-o',
};

export const CAMPAIGN_STATUS_COLOR: Record<string, string> = {
  roster: '#9e9e9e',
  template: '#607d8b',
  private_draft: '#d97706',
  draft: '#ff9800',
  in_progress: '#4caf50',
  completed: '#2196f3',
  trashed: '#f44336',
};

export const CAMPAIGN_STATUS_LABEL: Record<string, string> = {
  roster: 'Roster',
  template: 'Template',
  private_draft: 'Private draft',
  draft: 'Draft',
  in_progress: 'In progress',
  completed: 'Completed',
  trashed: 'Trashed',
};

export function CampaignStatusIcon({ status }: { status?: string }) {
  const style = useCampaignStatusStyle();
  const icon = CAMPAIGN_STATUS_ICON[status ?? ''] ?? 'fa-circle-o';
  const color = CAMPAIGN_STATUS_COLOR[status ?? ''] ?? '#999';

  if (style === 'dot') {
    const dotClass = CAMPAIGN_STATUS_DOT_CLASS[status ?? ''] ?? 'bg-ggc-border-gray';
    const label = CAMPAIGN_STATUS_LABEL[status ?? ''] ?? status ?? '';
    return (
      <Tooltip content={label}>
        <span
          className={`inline-block size-[7px] rounded-full mx-1 shrink-0 cursor-default ${dotClass}`}
        />
      </Tooltip>
    );
  }

  return <i className={`fa ${icon} mr-2 w-3.5 text-center`} style={{ color }} />;
}

const ALL_STATUS_ORDER = ['roster', 'template', 'private_draft', 'draft', 'in_progress', 'completed', 'trashed'];

export function campaignStatusSortWeight(status?: string): number {
  switch (status) {
    case 'in_progress': return -4;
    case 'draft': return -3;
    case 'template': return -2;
    case 'roster': return -1;
    default: {
      const idx = ALL_STATUS_ORDER.indexOf(status ?? '');
      return idx >= 0 ? idx : 99;
    }
  }
}

/* ══════════════════════════════════════════════════════════
   공통: 최근 캠페인 섹션 (CampaignSelectPanel / CampaignSelect 모두 사용)
   ══════════════════════════════════════════════════════════ */

export function useRecentCampaigns() {
  const [recentIds, setRecentIds] = useState(getRecentCampaignIds);
  const [expanded, setExpanded] = useState(true);

  const add = useCallback((id: string) => {
    setRecentIds(addRecentCampaignId(id));
  }, []);

  const remove = useCallback((id: string) => {
    setRecentIds(removeRecentCampaignId(id));
  }, []);

  const toggle = useCallback(() => setExpanded((v) => !v), []);

  return { recentIds, expanded, add, remove, toggle };
}


/* ══════════════════════════════════════════════════════════
   1. CampaignSelectPanel (커스텀 CSS 기반 단일 선택)
   ══════════════════════════════════════════════════════════ */

export interface CampaignSelectPanelProps {
  open: boolean;
  onClose: () => void;
  campaigns: CampaignItem[];
  loading?: boolean;
  onSelect: (id: string) => void;
  selectedId: string;
  side?: 'left' | 'right';
  showAllOption?: boolean;
  allOptionId?: string;
  showGenreFilter?: boolean;
  showCheckmark?: boolean;
  showAllNone?: boolean;
  selectedFirst?: boolean;
  admins?: Record<string, { profile?: { name?: string } }>;
  filterPlaceholder?: string;
  excludeCompleted?: boolean;
  title?: string;
  filterDescription?: string;
  /** 드롭다운 위치 기준 요소 (미지정 시 화면 중앙 드롭다운) */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** 제공 시 선택 후 패널을 닫지 않고 하단 확인 버튼을 표시 */
  onConfirm?: (id: string) => void;
  confirmLabel?: string;
}

export function CampaignSelectPanel({
  open,
  onClose,
  campaigns,
  loading = false,
  onSelect,
  selectedId,
  side = 'left',
  showAllOption = true,
  allOptionId = 'all',
  showGenreFilter = false,
  showCheckmark = false,
  showAllNone = false,
  selectedFirst = false,
  admins,
  filterPlaceholder,
  excludeCompleted = false,
  title,
  filterDescription,
  anchorRef,
  onConfirm,
  confirmLabel,
}: CampaignSelectPanelProps) {
  const [search, setSearch] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [legendOpen, setLegendOpen] = useState(false);
  const { t } = useI18nText();
  const statusStyle = useCampaignStatusStyle();

  const storeAdmins = useAppSelector((s) => s.envs.admins) as Record<string, { profile?: { name?: string } }>;
  const adminMap = useMemo(() => admins ?? storeAdmins ?? {}, [admins, storeAdmins]);

  const campaignGenres = useAppSelector((s) => s.envs.campaignGenres) as CampaignGenre[];
  const sortedGenres = useMemo(
    () => [...(campaignGenres ?? [])].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '')),
    [campaignGenres],
  );

  const baseCampaigns = useMemo(() => {
    let list = campaigns;
    if (excludeCompleted) list = list.filter((c) => c.status !== 'completed');
    if (!showHidden) list = list.filter((c) => !c.hidden);
    return list;
  }, [campaigns, excludeCompleted, showHidden]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of baseCampaigns) {
      const s = c.status ?? '';
      counts[s] = (counts[s] ?? 0) + 1;
    }
    return counts;
  }, [baseCampaigns]);

  const filtered = useMemo(() => {
    let list = campaigns;
    if (excludeCompleted) list = list.filter((c) => c.status !== 'completed');
    if (!showHidden) list = list.filter((c) => !c.hidden);
    if (statusFilter) list = list.filter((c) => c.status === statusFilter);
    if (search) {
      const q = search.toLowerCase().trim();
      list = list.filter((c) => {
        const name = (c.name ?? '').toLowerCase();
        const status = (c.status ?? '').replace(/_/g, ' ').toLowerCase();
        const owner = (adminMap[c.userId ?? '']?.profile?.name ?? '').toLowerCase();
        return name.includes(q) || status.includes(q) || owner.includes(q);
      });
    }
    if (showGenreFilter && selectedGenres.length > 0) {
      list = list.filter((c) => c.campaignGenre && selectedGenres.includes(c.campaignGenre));
    }
    const sorted = [...list].sort((a, b) => {
      const ws = campaignStatusSortWeight(a.status) - campaignStatusSortWeight(b.status);
      if (ws !== 0) return ws;
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
    if (selectedFirst) {
      const sel = sorted.filter((c) => c._id === selectedId);
      const rest = sorted.filter((c) => c._id !== selectedId);
      return [...sel, ...rest];
    }
    return sorted;
  }, [campaigns, search, showHidden, statusFilter, showGenreFilter, selectedGenres, selectedFirst, selectedId, excludeCompleted, adminMap]);

  const recent = useRecentCampaigns();

  const handleSelect = useCallback((id: string) => {
    recent.add(id);
    onSelect(id);
    if (!onConfirm) onClose();
  }, [recent, onSelect, onClose, onConfirm]);

  /* ── 키보드 네비게이션 ── */
  const [hlIdx, setHlIdx] = useState(-1);
  const bodyRef = useRef<HTMLDivElement>(null);

  // 선택 가능한 ID 목록 (all 옵션 + 필터된 캠페인)
  const selectableIds = useMemo(() => {
    const ids: string[] = [];
    if (showAllOption) ids.push(allOptionId);
    for (const c of filtered) ids.push(c._id);
    return ids;
  }, [showAllOption, allOptionId, filtered]);

  // 검색어 변경 시 하이라이트 리셋
  useEffect(() => { setHlIdx(-1); }, [search, statusFilter]);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHlIdx((prev) => (prev < selectableIds.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHlIdx((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && hlIdx >= 0 && hlIdx < selectableIds.length) {
      e.preventDefault();
      handleSelect(selectableIds[hlIdx]);
    }
  }, [selectableIds, hlIdx, handleSelect, onClose]);

  // 하이라이트 항목 스크롤
  useEffect(() => {
    if (hlIdx < 0 || !bodyRef.current) return;
    const el = bodyRef.current.querySelector(`[data-campaign-idx="${hlIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [hlIdx]);

  // 드롭다운 위치 계산 (paint 전에 실행해 레이아웃 시프트 방지)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({ position: 'fixed', top: -9999, left: -9999 });
  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const panelWidth = 380;
    let left = rect.left;
    // 오른쪽 넘침 방지
    if (left + panelWidth > window.innerWidth - 8) {
      left = window.innerWidth - panelWidth - 8;
    }
    if (left < 8) left = 8;

    // 아래 공간이 부족하면 위쪽으로 열기
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const openAbove = spaceBelow < 200 && spaceAbove > spaceBelow;

    if (openAbove) {
      setDropdownStyle({
        position: 'fixed',
        bottom: window.innerHeight - rect.top + 4,
        left,
        width: panelWidth,
        maxHeight: `${spaceAbove}px`,
      });
    } else {
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left,
        width: panelWidth,
        maxHeight: `${spaceBelow}px`,
      });
    }
  }, [open, anchorRef]);

  if (!open) return null;

  const isDropdown = !!anchorRef?.current;

  const handleGenreToggle = (genreId: string) => {
    setSelectedGenres((prev) =>
      prev.includes(genreId) ? prev.filter((g) => g !== genreId) : [...prev, genreId],
    );
  };

  const panelContent = (
    <>
      <div className="drawer-header">
        <span>
          {title ?? t('Select Campaign', '캠페인 선택')}
          {filtered.length !== baseCampaigns.length ? (
            <>
              <Tooltip content={t('Filtered campaigns', '필터링된 캠페인')}>
                <span className="drawer-count drawer-count--filtered">{filtered.length}</span>
              </Tooltip>
              {' / '}
              <Tooltip content={t('Total campaigns', '전체 캠페인')}>
                <span className="drawer-count">{baseCampaigns.length}</span>
              </Tooltip>
            </>
          ) : (
            <Tooltip content={t('Total campaigns', '전체 캠페인')}>
              <span className="drawer-count">{baseCampaigns.length}</span>
            </Tooltip>
          )}
        </span>
        <button className="drawer-close" onClick={onClose}>
          <i className="fa fa-times" />
        </button>
      </div>

      {/* ── 필터 상태 안내 ── */}
      <div className="drawer-filter-info">
        <i className="fa fa-info-circle text-[#5b9bd5] text-xs" />
        <span>
          {filterDescription ?? (excludeCompleted
            ? t('Completed campaigns are excluded.', '완료 캠페인 제외됨.')
            : t('Showing all status campaigns.', '모든 상태의 캠페인 표시.'))}
          {!showHidden && ` ${t('Hidden not shown.', '숨김 캠페인 미표시.')}`}
          {statusFilter && ` ${t('Status', '상태')}: ${statusFilter.replace(/_/g, ' ')}`}
        </span>
      </div>

        <div className="drawer-legend-wrapper">
          <button
            type="button"
            className="drawer-legend-toggle"
            onClick={() => setLegendOpen((v) => !v)}
          >
            <span className="drawer-legend-toggle-label">
              {t('Status filter', '상태 필터')}
              {statusFilter && (
                <span className="drawer-legend-active-tag">{statusFilter.replace(/_/g, ' ')}</span>
              )}
            </span>
            <i className={`fa fa-caret-${legendOpen ? 'up' : 'down'}`} />
          </button>
          {legendOpen && (
            <div className="drawer-legend">
              {Object.entries(CAMPAIGN_STATUS_ICON).map(([status, icon]) => (
                <Tooltip key={status} content={status.replace(/_/g, ' ')}>
                  <button
                    type="button"
                    className={`drawer-legend-item${statusFilter === status ? ' drawer-legend-item--active' : ''}`}
                    onClick={() => setStatusFilter((prev) => (prev === status ? null : status))}
                  >
                    {statusStyle === 'dot' ? (
                      <span
                        className={`inline-block size-[7px] rounded-full shrink-0 ${CAMPAIGN_STATUS_DOT_CLASS[status] ?? 'bg-ggc-border-gray'}`}
                        aria-hidden
                      />
                    ) : (
                      <i className={`fa ${icon}`} style={{ color: CAMPAIGN_STATUS_COLOR[status] ?? '#999' }} />
                    )}
                    <span className="drawer-legend-label">{status.replace(/_/g, ' ')} ({statusCounts[status] ?? 0})</span>
                  </button>
                </Tooltip>
              ))}
            </div>
          )}
        </div>

        <div className="drawer-search">
          <input
            className="filter-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder={filterPlaceholder ?? t('Name/Status/Owner', 'Name/Status/Owner')}
            autoFocus
          />
          <div className="drawer-filter-row">
            <GgcCheckbox
              className="drawer-show-hidden"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
            >
              <i className="fa fa-eye-slash" />
            </GgcCheckbox>
            {showAllNone && (
              <span className="drawer-allnone">
                <button
                  className="drawer-allnone-btn drawer-allnone-btn--all"
                  onClick={() => setSelectedGenres(sortedGenres.map((g) => g._id))}
                >
                  <i className="fa fa-check-square-o" /> {t('All', '전체')}
                </button>
                <button
                  className="drawer-allnone-btn drawer-allnone-btn--none"
                  onClick={() => setSelectedGenres([])}
                >
                  <i className="fa fa-square-o" /> {t('None', '해제')}
                </button>
              </span>
            )}
          </div>
          {showGenreFilter && sortedGenres.length > 0 && (
            <SelectPanelGenreDropdown
              genres={sortedGenres}
              selectedGenres={selectedGenres}
              onToggle={handleGenreToggle}
              t={t}
            />
          )}
        </div>

        <div className="drawer-body" ref={bodyRef}>
          {showAllOption && (
            <div
              data-campaign-idx={0}
              className={`drawer-item${selectedId === allOptionId ? ' drawer-item--active' : ''}${hlIdx === 0 ? ' drawer-item--highlight' : ''}`}
              onClick={() => handleSelect(allOptionId)}
            >
              <i className="fa fa-users text-[#1adf10] mr-2 w-3.5 text-center" />
              {t('All influencers', '전체 인플루언서')}
            </div>
          )}
          {/* 최근 조회 캠페인 */}
          {recent.recentIds.length > 0 && !search && !statusFilter && (() => {
            const recentCampaigns = recent.recentIds
              .map((id: string) => baseCampaigns.find((c) => c._id === id))
              .filter((c): c is CampaignItem => c != null);
            if (recentCampaigns.length === 0) return null;
            return (
              <div className="p-2">
                <div
                  className="campaign-dropdown-section-label campaign-dropdown-section-label--toggle flex items-center gap-2 whitespace-nowrap shrink-0"
                  onClick={recent.toggle}
                >
                  <i className={`fa fa-chevron-${recent.expanded ? 'down' : 'right'} campaign-dropdown-section-chevron`} />
                  <i className="fa fa-bookmark text-ggc-orange ml-2" />
                  <Tooltip
                    content={t(
                      'List of up to 10 campaigns from your recent searches.',
                      '최근 검색한 10개의 캠페인 목록입니다.',
                    )}
                  >
                    <span className="inline-flex items-center">
                      {t('Recent', '최근 조회')} ({recentCampaigns.length})
                    </span>
                  </Tooltip>
                </div>
                <div className="bg-ggc-sub-yellow-light rounded-ggc-lg">
                  {recent.expanded && recentCampaigns.map((c, idx) => (
                    <div
                      key={`recent-${c._id}`}
                      className={`drawer-item campaign-dropdown-item${selectedId === c._id ? ' drawer-item--active' : ''}`}
                      onClick={() => handleSelect(c._id)}
                    >
                      <span className="drawer-item-num">{idx + 1}</span>
                      <CampaignStatusIcon status={c.status} />
                      {c.name}
                      <button
                        type="button"
                        className="campaign-dropdown-recent-remove"
                        onClick={(e) => { e.stopPropagation(); recent.remove(c._id); }}
                        aria-label={t('Remove from recent', '최근 목록에서 제거')}
                      >
                        <i className="fa fa-times" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          {filtered.map((c, idx) => {
            const itemIdx = showAllOption ? idx + 1 : idx;
            return (
            <div
              key={c._id}
              data-campaign-idx={itemIdx}
              className={`drawer-item${selectedId === c._id ? ' drawer-item--active' : ''}${hlIdx === itemIdx ? ' drawer-item--highlight' : ''}`}
              onClick={() => handleSelect(c._id)}
            >
              <span className="drawer-item-num">{idx + 1}</span>
              {showCheckmark && (
                <i
                  className={`fa w-[18px] mr-1.5 text-center text-ggc-green${selectedId === c._id ? ' fa-check' : ''}`}
                />
              )}
              <CampaignStatusIcon status={c.status} />
              {c.name}
            </div>
            );
          })}
          {loading && (
            <div className="drawer-loading">
              <i className="fa fa-spinner fa-spin drawer-loading__icon" />
              <span>{t('Loading campaigns...', '캠페인 불러오는 중...')}</span>
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="drawer-empty">
              {t('No campaigns found', '캠페인을 찾을 수 없습니다')}
            </div>
          )}
        </div>
      {onConfirm && (
        <div className="drawer-confirm-footer">
          <button
            type="button"
            className="drawer-confirm-btn"
            disabled={!selectedId}
            onClick={() => { if (selectedId) { onConfirm(selectedId); onClose(); } }}
          >
            {confirmLabel ?? t('Add to campaign', '캠페인에 추가')}
          </button>
        </div>
      )}
    </>
  );

  if (isDropdown) {
    // Portaled to <body>: `position: fixed` is computed relative to the nearest
    // ancestor with a `transform` (or filter/perspective/will-change), not the
    // viewport, if one exists — e.g. a Radix Popover.Content, which animates via
    // `transform` for its zoom-in/out. Without the portal, anchoring this inside
    // one (as AddInfluencersPopover's "Add to another campaign" option does)
    // silently breaks `dropdownStyle`'s viewport-relative math and/or clips the
    // panel to that ancestor's bounds (see docs' recurring "popover clipped by an
    // overflow/transform ancestor" note).
    return createPortal(
      <>
        <div className="campaign-select-overlay" onClick={onClose} />
        <div className="campaign-select-dropdown" style={dropdownStyle}>
          {panelContent}
        </div>
      </>,
      document.body,
    );
  }

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <div className={`drawer drawer--${side}`}>
        {panelContent}
      </div>
    </>
  );
}

/* ── CampaignSelectPanel 내부: 장르 드롭다운 ── */

function SelectPanelGenreDropdown({
  genres, selectedGenres, onToggle, t,
}: {
  genres: CampaignGenre[];
  selectedGenres: string[];
  onToggle: (id: string) => void;
  t: (en: string, ko: string) => string;
}) {
  const [dropOpen, setDropOpen] = useState(false);
  const label = selectedGenres.length === 0
    ? t('Filter by campaign genre', '캠페인 장르로 필터')
    : `${selectedGenres.length} ${t('genre(s) selected', '개 장르 선택됨')}`;

  return (
    <div className="drawer-genre-dropdown">
      <button className="drawer-genre-toggle" onClick={() => setDropOpen((v) => !v)}>
        <span className="drawer-genre-label">{label}</span>
        <i className={`fa fa-caret-${dropOpen ? 'up' : 'down'}`} />
      </button>
      {dropOpen && (
        <div className="drawer-genre-menu">
          {genres.map((g) => (
            <GgcCheckbox
              key={g._id}
              className="drawer-genre-option"
              checked={selectedGenres.includes(g._id)}
              onChange={() => onToggle(g._id)}
            >
              {g.title ?? g._id}
            </GgcCheckbox>
          ))}
          {genres.length === 0 && (
            <div className="drawer-genre-empty">{t('No genres', '장르 없음')}</div>
          )}
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════
   2. CampaignDrawer (shadcn 기반 단일/다중 선택 + 트리거)
   ══════════════════════════════════════════════════════════ */

interface CampaignSelectSingleProps {
  multiple?: false;
  value: string;
  onChange: (id: string, campaign?: SelectableCampaign) => void;
}

interface CampaignSelectMultiProps {
  multiple: true;
  value: string[];
  onChange: (ids: string[], campaigns?: SelectableCampaign[]) => void;
}

type CampaignSelectProps = (CampaignSelectSingleProps | CampaignSelectMultiProps) & {
  campaigns: SelectableCampaign[];
  loading?: boolean;
  placeholder?: string;
  showAll?: boolean;
  allLabel?: string;
  excludeCompleted?: boolean;
  side?: 'left' | 'right';
  admins?: Record<string, { profile?: { name?: string } }>;
  campaignGenres?: CampaignGenreOption[];
  filterDescription?: string;
  className?: string;
};

/* ── 내부: 드로어 리스트 ── */

interface DropdownListProps {
  campaigns: SelectableCampaign[];
  loading: boolean;
  selectedIds: string[];
  multiple: boolean;
  showAll: boolean;
  allLabel: string;
  excludeCompleted: boolean;
  admins: Record<string, { profile?: { name?: string } }>;
  campaignGenres: CampaignGenreOption[];
  filterDescription?: string;
  onSelect: (campaign: SelectableCampaign | null) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  onClose: () => void;
  side: 'left' | 'right';
}

function ShadcnDropdownList({
  campaigns, loading, selectedIds, multiple, showAll, allLabel,
  excludeCompleted, admins, campaignGenres, filterDescription,
  onSelect, onSelectAll, onSelectNone, onClose, side,
}: DropdownListProps) {
  const { t } = useI18nText();
  const [filter, setFilter] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [selectedGenreIds, setSelectedGenreIds] = useState<string[]>([]);
  const filterRef = useRef<HTMLInputElement>(null);

  const recent = useRecentCampaigns();
  const handleSelect = useCallback((c: SelectableCampaign | null) => {
    if (c) recent.add(c.id);
    onSelect(c as never);
  }, [onSelect, recent]);

  useEffect(() => { filterRef.current?.focus(); }, []);

  /* ── 키보드 네비게이션 (state/ref 선언만) ── */
  const [hlIdx, setHlIdx] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  const overlayRef = useRef<HTMLDivElement>(null);
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let list = [...campaigns];
    if (excludeCompleted) list = list.filter((c) => c.status !== 'completed');
    if (!showHidden) list = list.filter((c) => !c.hidden);
    if (q) {
      list = list.filter((c) => {
        const name = c.name.toLowerCase();
        const status = (c.status ?? '').replace(/_/g, ' ').toLowerCase();
        const owner = (admins[c.userId ?? '']?.profile?.name ?? '').toLowerCase();
        return name.includes(q) || status.includes(q) || owner.includes(q);
      });
    }
    if (selectedGenreIds.length > 0) {
      list = list.filter((c) => c.campaignGenre && selectedGenreIds.includes(c.campaignGenre));
    }
    return list.sort((a, b) => {
      const ws = campaignStatusSortWeight(a.status) - campaignStatusSortWeight(b.status);
      if (ws !== 0) return ws;
      return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
    });
  }, [campaigns, filter, showHidden, excludeCompleted, admins, selectedGenreIds]);

  const { selectedItems, unselectedItems } = useMemo(() => {
    if (!multiple) return { selectedItems: [], unselectedItems: filtered };
    return {
      selectedItems: filtered.filter((c) => selectedIds.includes(c.id)),
      unselectedItems: filtered.filter((c) => !selectedIds.includes(c.id)),
    };
  }, [filtered, selectedIds, multiple]);

  const sortedGenres = useMemo(() => [...campaignGenres].sort((a, b) => a.title.localeCompare(b.title)), [campaignGenres]);
  const genreOptions: MultiSelectOption[] = useMemo(() => sortedGenres.map((g) => ({ value: g._id, label: g.title })), [sortedGenres]);

  /* ── 키보드 네비게이션 (filtered/selectedItems 이후) ── */
  const selectableList = useMemo(() => {
    const items: (SelectableCampaign | null)[] = [];
    if (showAll && !multiple) items.push(null);
    if (multiple) {
      items.push(...selectedItems, ...unselectedItems);
    } else {
      items.push(...filtered);
    }
    return items;
  }, [showAll, multiple, filtered, selectedItems, unselectedItems]);

  useEffect(() => { setHlIdx(-1); }, [filter]);

  const handleFilterKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHlIdx((prev) => (prev < selectableList.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHlIdx((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && hlIdx >= 0 && hlIdx < selectableList.length) {
      e.preventDefault();
      handleSelect(selectableList[hlIdx]);
    }
  }, [selectableList, hlIdx, handleSelect, onClose]);

  useEffect(() => {
    if (hlIdx < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-shadcn-idx="${hlIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [hlIdx]);

  const drawerClass = side === 'right' ? 'right-0 border-l' : 'left-0 border-r';

  const renderWithGroups = (items: SelectableCampaign[]) =>
    items.map((c, idx) => {
      const showHeader = !filter && c.status !== items[idx - 1]?.status;
      return (
        <Fragment key={c.id}>
          {showHeader && (
            <div className="px-4 pt-2 pb-0.5">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-ggc-sm text-white whitespace-nowrap shrink-0 ggc-text-badge"
                style={{ backgroundColor: CAMPAIGN_STATUS_COLOR[c.status ?? ''] ?? '#999' }}
              >
                <i className={`fa ${CAMPAIGN_STATUS_ICON[c.status ?? ''] ?? 'fa-circle-o'}`} />
                {CAMPAIGN_STATUS_LABEL[c.status ?? ''] ?? (c.status ?? '').replace(/_/g, ' ')}
              </span>
            </div>
          )}
          {renderItem(c)}
        </Fragment>
      );
    });

  // selectableList 에서 캠페인 id → index 매핑
  const selectableIdxMap = useMemo(() => {
    const map = new Map<string, number>();
    selectableList.forEach((c, i) => { if (c) map.set(c.id, i); });
    return map;
  }, [selectableList]);

  const renderItem = (c: SelectableCampaign) => {
    const currentIdx = selectableIdxMap.get(c.id) ?? -1;
    const isSelected = selectedIds.includes(c.id);
    return (
      <button
        key={c.id}
        data-shadcn-idx={currentIdx}
        onClick={() => handleSelect(c)}
        className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition hover:bg-muted/40 ${
          isSelected ? 'text-ggc-cta-blue' : 'text-foreground'
        }${hlIdx === currentIdx ? ' bg-ggc-blue-light ring-1 ring-inset ring-blue-300' : ''}`}
      >
        {multiple ? (
          <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-ggc-sm border text-xs ${
            isSelected ? 'border-ggc-cta-blue bg-blue-600 text-white' : 'border-ggc-border-gray-dark'
          }`}>
            {isSelected && <Check className="h-3 w-3" />}
          </span>
        ) : (
          <span className="w-4 shrink-0">
            {isSelected && <Check className="h-3.5 w-3.5 text-ggc-cta-blue" />}
          </span>
        )}
        <CampaignStatusIcon status={c.status} />
        <span className={`flex-1 truncate ${isSelected && multiple ? 'font-medium' : ''}`}>{c.name}</span>
        {c.status && (
          <span className="shrink-0 text-xs text-ggc-text-gray">{c.status.replace(/_/g, ' ')}</span>
        )}
      </button>
    );
  };

  return (
    <div ref={overlayRef} onClick={handleOverlayClick} className="fixed inset-0 bg-black/20 z-[1035]">
      <div className={`absolute top-0 bottom-0 flex w-[400px] flex-col bg-white shadow-2xl max-h-[100dvh] z-[1036] ${drawerClass}`}>
        {/* 헤더 */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <span className="flex-1 text-sm font-semibold text-foreground">
            {multiple ? t('Select campaigns', '캠페인 선택') : t('Select campaign', '캠페인 선택')}
          </span>
          <span className="text-xs text-ggc-text-gray">{filtered.length} / {campaigns.length}</span>
          {multiple && (
            <span className="rounded-ggc-pill bg-ggc-blue-light px-2 py-0.5 text-xs font-medium text-ggc-cta-blue">
              {selectedIds.length} {t('selected', '선택됨')}
            </span>
          )}
          <button onClick={onClose} className="rounded-ggc-sm p-1 hover:bg-muted/50"><X className="h-4 w-4" /></button>
        </div>

        {/* 필터 안내 */}
        <div className="flex items-center gap-1.5 border-b border-border bg-ggc-blue-light/60 px-4 py-1.5">
          <Info className="h-3.5 w-3.5 shrink-0 text-primary-600" />
          <span className="text-ggc-cta-blue ggc-text-caption-m">
            {filterDescription ?? (excludeCompleted
              ? t('Completed campaigns are excluded.', '완료 캠페인 제외됨.')
              : t('Showing all status campaigns.', '모든 상태의 캠페인 표시.'))}
            {!showHidden && ` ${t('Hidden not shown.', '숨김 캠페인 미표시.')}`}
            {selectedGenreIds.length > 0 && ` ${t('Filtered by', '장르 필터')} ${selectedGenreIds.length}${t(' genre(s).', '개.')}`}
          </span>
        </div>

        {/* 검색 + 숨김 토글 */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-2">
          <GgcInput ref={filterRef} value={filter} onChange={(e) => setFilter(e.target.value)} onKeyDown={handleFilterKeyDown} placeholder={t('Filter by campaign name...', '캠페인 이름으로 검색...')} className="h-8 flex-1 text-sm" />
          <button
            onClick={() => setShowHidden((v) => !v)}
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-ggc-sm border transition ${showHidden ? 'border-ggc-cta-blue bg-ggc-blue-light text-ggc-cta-blue' : 'border-ggc-border-gray-dark text-ggc-text-gray hover:border-ggc-border-gray-dark'}`}
            title={t('Show hidden campaigns', '숨김 캠페인 표시')}
          >
            <EyeOff className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* 장르 필터 */}
        {genreOptions.length > 0 && (
          <div className="flex items-center gap-2 border-b border-border px-4 py-2">
            <div className="flex-1">
              <MultiSelect options={genreOptions} value={selectedGenreIds} onChange={setSelectedGenreIds} placeholder={t('Filter by genre...', '장르 필터...')} maxTagCount={3} />
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <button onClick={() => setSelectedGenreIds(genreOptions.map((g) => g.value))} className="text-xs text-ggc-guide-blue hover:underline">{t('All', '전체')}</button>
              <span className="text-xs text-ggc-border-gray-dark">/</span>
              <button onClick={() => setSelectedGenreIds([])} className="text-xs text-ggc-text-gray hover:underline">{t('None', '없음')}</button>
            </div>
          </div>
        )}

        {/* Quick select (다중 선택) */}
        {multiple && (
          <div className="flex items-center gap-3 border-b border-border px-4 py-1.5">
            <span className="text-xs text-ggc-text-gray">{t('Quick select', '빠른 선택')}:</span>
            <button onClick={onSelectAll} className="flex items-center gap-1 text-xs text-ggc-guide-blue hover:underline"><Check className="h-3 w-3" /> {t('All', '전체')}</button>
            <button onClick={onSelectNone} className="flex items-center gap-1 text-xs text-ggc-text-gray hover:underline"><span className="inline-block h-3 w-3 rounded-sm border border-gray-400" /> {t('None', '없음')}</button>
          </div>
        )}

        {/* 목록 */}
        <div className="flex-1 overflow-y-auto" ref={listRef}>
          {!loading && showAll && !multiple && (
            <button
              data-shadcn-idx={0}
              onClick={() => handleSelect(null)}
              className={`flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm transition hover:bg-muted/40 ${selectedIds.length === 0 ? 'font-medium text-ggc-green' : 'text-ggc-green'}${hlIdx === 0 ? ' bg-ggc-blue-light ring-1 ring-inset ring-blue-300' : ''}`}
            >
              <span className="w-4 shrink-0">{selectedIds.length === 0 && <Check className="h-3.5 w-3.5" />}</span>
              <Users className="h-3.5 w-3.5 shrink-0" />
              {allLabel}
            </button>
          )}

          {/* 최근 조회 캠페인 */}
          {!loading && recent.recentIds.length > 0 && !filter && (() => {
            const recentCampaigns = recent.recentIds
              .map((id: string) => campaigns.find((c) => c.id === id))
              .filter((c): c is SelectableCampaign => c != null);
            if (recentCampaigns.length === 0) return null;
            return (
              <div className="bg-ggc-sub-yellow-light mb-2 rounded-md p-2">
                <div className="campaign-dropdown-section-label campaign-dropdown-section-label--toggle flex items-center gap-2 whitespace-nowrap shrink-0" onClick={recent.toggle}>
                  <i className={`fa fa-chevron-${recent.expanded ? 'down' : 'right'} text-[9px] w-[10px] text-center`} />
                  <i className="fa fa-bookmark text-ggc-orange ml-2" />
                  <Tooltip
                    content={t(
                      'List of up to 10 campaigns from your recent searches.',
                      '최근 검색한 10개의 캠페인 목록입니다.',
                    )}
                  >
                    <span className="inline-flex items-center">
                      {t('Recent', '최근 조회')} ({recentCampaigns.length})
                    </span>
                  </Tooltip>
                </div>
                {recent.expanded && recentCampaigns.map((c, idx) => {
                  const isSelected = selectedIds.includes(c.id);
                  return (
                    <div
                      key={`recent-${c.id}`}
                      className={`campaign-dropdown-item flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition hover:bg-muted/40 cursor-pointer ${isSelected ? 'text-ggc-cta-blue' : 'text-foreground'}`}
                      onClick={() => handleSelect(c)}
                    >
                      {multiple ? (
                        <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-ggc-sm border text-xs ${isSelected ? 'border-ggc-cta-blue bg-blue-600 text-white' : 'border-ggc-border-gray-dark'}`}>
                          {isSelected && <Check className="h-3 w-3" />}
                        </span>
                      ) : (
                        <span className="w-4 shrink-0">{isSelected && <Check className="h-3.5 w-3.5 text-ggc-cta-blue" />}</span>
                      )}
                      <span className="drawer-item-num mr-0 min-w-[1.5rem] text-center">{idx + 1}</span>
                      <CampaignStatusIcon status={c.status} />
                      <span className={`flex-1 truncate ${isSelected && multiple ? 'font-medium' : ''}`}>{c.name}</span>
                      <button
                        type="button"
                        className="campaign-dropdown-recent-remove"
                        onClick={(e) => { e.stopPropagation(); recent.remove(c.id); }}
                        aria-label={t('Remove from recent', '최근 목록에서 제거')}
                      >
                        <i className="fa fa-times" />
                      </button>
                    </div>
                  );
                })}
                <div className="border-b border-border my-1" />
              </div>
            );
          })()}

          {/* 다중 선택: 선택됨 */}
          {!loading && multiple && selectedItems.length > 0 && (
            <>
              <div className="bg-ggc-blue-light/60 px-4 py-1 text-[11px] font-semibold uppercase tracking-wide text-ggc-cta-blue">
                {t('Selected', '선택됨')} ({selectedItems.length})
              </div>
              {selectedItems.map(renderItem)}
              {unselectedItems.length > 0 && <div className="border-b border-dashed border-border" />}
            </>
          )}

          {/* 전체 목록 */}
          {!loading && (multiple ? unselectedItems : filtered).length > 0 && (
            <>
              {multiple && selectedItems.length > 0 && unselectedItems.length > 0 && (
                <div className="bg-muted/30 px-4 py-1 text-[11px] font-semibold uppercase tracking-wide text-ggc-text-gray">
                  {t('All campaigns', '모든 캠페인')}
                </div>
              )}
              {renderWithGroups(multiple ? unselectedItems : filtered)}
            </>
          )}

          {loading && (
            <div className="drawer-loading">
              <i className="fa fa-spinner fa-spin drawer-loading__icon" />
              <span>{t('Loading campaigns...', '캠페인 불러오는 중...')}</span>
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="py-12 text-center text-sm text-ggc-text-gray">
              {t('No campaigns found', '캠페인을 찾을 수 없습니다')}
            </div>
          )}
        </div>

        {/* Done 버튼 (다중 선택) */}
        {multiple && (
          <div className="border-t border-border px-4 py-3">
            <GgcButton className="w-full" onClick={onClose}>Done ({selectedIds.length} selected)</GgcButton>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── 트리거 버튼 ── */

function TriggerButton({ label, onClick, className }: { label: string; onClick: () => void; className?: string }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex min-w-[160px] h-9 items-center justify-between gap-2 rounded-md border border-input bg-white px-3 text-sm font-normal transition hover:border-ggc-disabled focus:outline-none focus:border-ggc-cta-blue focus:ring-1 focus:ring-blue-500 ${className ?? ''}`}
    >
      <span className="truncate">{label}</span>
      <ChevronDown className="h-4 w-4 shrink-0 text-ggc-text-gray" />
    </button>
  );
}

/* ── 메인 컴포넌트 ── */

export default function CampaignSelect(props: CampaignSelectProps) {
  const { t } = useI18nText();
  const {
    campaigns, loading = false,
    placeholder = t('Select campaign...', '캠페인 선택...'),
    showAll = false, allLabel = t('All influencers', '모든 influencer'),
    excludeCompleted = false, side = 'left',
    admins = {}, campaignGenres = [], filterDescription, className,
  } = props;

  const [open, setOpen] = useState(false);

  const selectedIds = useMemo<string[]>(
    () => (props.multiple ? (props.value ?? []) : props.value ? [props.value] : []),
    [props.multiple, props.value],
  );

  const campaignById = useMemo(() => new Map(campaigns.map((c) => [c.id, c])), [campaigns]);

  const triggerLabel = useMemo(() => {
    if (selectedIds.length === 0) return props.multiple ? placeholder : (showAll ? allLabel : placeholder);
    if (selectedIds.length === 1) return campaignById.get(selectedIds[0])?.name ?? placeholder;
    return `${t('Selected', '선택됨')} (${selectedIds.length})`;
  }, [selectedIds, campaignById, placeholder, props.multiple, showAll, allLabel]);

  const handleSelect = useCallback((campaign: SelectableCampaign | null) => {
    if (props.multiple) {
      const id = campaign?.id ?? '';
      const next = selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id];
      const nextCampaigns = next.map((i) => campaignById.get(i)).filter(Boolean) as SelectableCampaign[];
      props.onChange(next, nextCampaigns);
    } else {
      props.onChange(campaign?.id ?? '', campaign ?? undefined);
      setOpen(false);
    }
  }, [props, selectedIds, campaignById]);

  const handleSelectAll = useCallback(() => {
    if (!props.multiple) return;
    props.onChange(campaigns.map((c) => c.id), campaigns);
  }, [props, campaigns]);

  const handleSelectNone = useCallback(() => {
    if (!props.multiple) return;
    props.onChange([], []);
  }, [props]);

  return (
    <>
      <TriggerButton label={triggerLabel} onClick={() => setOpen(true)} className={className} />
      {open && (
        <ShadcnDropdownList
          campaigns={campaigns} loading={loading} selectedIds={selectedIds}
          multiple={props.multiple ?? false} showAll={showAll} allLabel={allLabel}
          excludeCompleted={excludeCompleted} admins={admins} campaignGenres={campaignGenres}
          filterDescription={filterDescription}
          onSelect={handleSelect} onSelectAll={handleSelectAll} onSelectNone={handleSelectNone}
          onClose={() => setOpen(false)} side={side}
        />
      )}
    </>
  );
}
